import { c as _c } from "react/compiler-runtime";
import { Stack, Typography } from '@mui/material';
import { type Condition, ConditionOperatorEnum } from '@repo/api-gw-sdk';
import { useMemo, useState, type UIEventHandler } from 'react';
import { AddButton } from '@/components/buttons/addButton';
import { createEmptyCondition } from '@/components/conditions/conditionFactory';
import { FilterOperatorSelect } from '@/components/conditions/filterOperatorSelect';
import { SelectionPanel } from '@/components/panels/selectionPanel/selectionPanel';
import { ScrollableContent } from '@/components/scroll/scrollableContent';
import { ScrollableWrapper } from '@/components/scroll/scrollableContext';
import { ScrollableHeader } from '@/components/scroll/scrollableHeader';
import { useScrollable } from '@/components/scroll/useScrollable';
import { createCombineCondition, findValueCondition, type FilterOption } from '@/types/advanceFilter';
import { pluralize } from '@/utils/string';
import { tagEntityLabel } from './tagKeyValueUtils';
import { TagSearchBoxes } from './tagSearchBoxes';
import type { TagsDataResult, TagsFilterProps } from './tagsFilter';
import { useResourceTagsData, useSourceAccountTagsData } from './tagsFilter';
import { useTagOptions } from './useTagOptions';
import { defaultOperatorFor, MultiSelectFilter } from '../filters';
import { CLOUD_PROVIDER_PROPERTY } from '../propertyNames';

/** Keeps the operator the condition already carries; only a brand-new condition gets the default. */
const withSelectedTags = (props: TagsFilterProps, values: string[]): Condition[] => {
  const existingCondition = findValueCondition(props.conditions, props.property.name);
  return props.conditions.filter(x => x !== existingCondition).concat(values.length ? [{
    property: props.property.name,
    operator: existingCondition?.operator ?? defaultOperatorFor(props.property),
    value: values
  }] : []);
};
function useProviderFilter(props) {
  const $ = _c(2);
  let t0;
  bb0: {
    if (!props.supportedProviders?.length) {
      t0 = undefined;
      break bb0;
    }
    let t1;
    if ($[0] !== props.supportedProviders) {
      t1 = createCombineCondition([{
        property: CLOUD_PROVIDER_PROPERTY,
        operator: ConditionOperatorEnum.In,
        value: props.supportedProviders
      }]);
      $[0] = props.supportedProviders;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    t0 = t1;
  }
  return t0;
}
const ResourceTagsConditionWrapper = props => {
  const $ = _c(3);
  const filter = useProviderFilter(props);
  const tagsData = useResourceTagsData(filter);
  let t0;
  if ($[0] !== props || $[1] !== tagsData) {
    t0 = <TagsConditionContent {...props} tagsData={tagsData} />;
    $[0] = props;
    $[1] = tagsData;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
const SourceAccountTagsConditionWrapper = props => {
  const $ = _c(3);
  const filter = useProviderFilter(props);
  const tagsData = useSourceAccountTagsData(filter);
  let t0;
  if ($[0] !== props || $[1] !== tagsData) {
    t0 = <TagsConditionContent {...props} tagsData={tagsData} />;
    $[0] = props;
    $[1] = tagsData;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export const TagsCondition = props => {
  const $ = _c(4);
  if (props.dataSource === "sourceAccountTags") {
    let t0;
    if ($[0] !== props) {
      t0 = <SourceAccountTagsConditionWrapper {...props} />;
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] !== props) {
    t0 = <ResourceTagsConditionWrapper {...props} />;
    $[2] = props;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};
const TagsConditionContent = props => {
  const $ = _c(35);
  const [keyFilter, setKeyFilter] = useState("");
  const [valueFilter, setValueFilter] = useState("");
  const {
    data: body,
    isLoading
  } = props.tagsData;
  let t0;
  if ($[0] !== props.conditions || $[1] !== props.property.name) {
    t0 = findValueCondition(props.conditions, props.property.name);
    $[0] = props.conditions;
    $[1] = props.property.name;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const condition = t0;
  let t1;
  if ($[3] !== condition?.value) {
    t1 = condition?.value ?? [];
    $[3] = condition?.value;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const selectedResources = t1;
  const t2 = body?.tags;
  let t3;
  if ($[5] !== keyFilter || $[6] !== props.initialFilterMode || $[7] !== selectedResources || $[8] !== t2 || $[9] !== valueFilter) {
    t3 = {
      tags: t2,
      selectedValues: selectedResources,
      keyFilter,
      valueFilter,
      mode: props.initialFilterMode
    };
    $[5] = keyFilter;
    $[6] = props.initialFilterMode;
    $[7] = selectedResources;
    $[8] = t2;
    $[9] = valueFilter;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const options = useTagOptions(t3);
  let t4;
  if ($[11] !== props.initialFilterMode) {
    t4 = tagEntityLabel(props.initialFilterMode);
    $[11] = props.initialFilterMode;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const entityLabel = t4;
  let t5;
  if ($[13] !== props || $[14] !== selectedResources) {
    t5 = item => {
      props.onChange(withSelectedTags(props, selectedResources.filter(x => x !== item)));
    };
    $[13] = props;
    $[14] = selectedResources;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== condition || $[17] !== props) {
    t6 = () => props.onChange(props.conditions.filter(x_0 => x_0 !== condition));
    $[16] = condition;
    $[17] = props;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== entityLabel || $[20] !== selectedResources || $[21] !== t5 || $[22] !== t6) {
    t7 = {
      items: selectedResources,
      entityType: entityLabel,
      onRemove: t5,
      renderItem: _temp,
      itemKey: _temp2,
      clearAll: t6
    };
    $[19] = entityLabel;
    $[20] = selectedResources;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== entityLabel || $[25] !== keyFilter || $[26] !== options || $[27] !== props || $[28] !== selectedResources || $[29] !== valueFilter) {
    t8 = <ScrollableWrapper><TagsConditionBody {...props} options={options} entityLabel={entityLabel} selectedResources={selectedResources} keyFilter={keyFilter} valueFilter={valueFilter} setKeyFilter={setKeyFilter} setValueFilter={setValueFilter} /></ScrollableWrapper>;
    $[24] = entityLabel;
    $[25] = keyFilter;
    $[26] = options;
    $[27] = props;
    $[28] = selectedResources;
    $[29] = valueFilter;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== isLoading || $[32] !== t7 || $[33] !== t8) {
    t9 = <SelectionPanel isLoading={isLoading} drawerProps={t7}>{t8}</SelectionPanel>;
    $[31] = isLoading;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  return t9;
};

/** Bridges the chip's operator picker to the `Condition[]` shape the tags panel works in. */
const TagsOperatorSelect = props => {
  const $ = _c(14);
  let t0;
  if ($[0] !== props.conditions || $[1] !== props.property.name) {
    t0 = findValueCondition(props.conditions, props.property.name);
    $[0] = props.conditions;
    $[1] = props.property.name;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const condition = t0;
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      marginY: "12px"
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== condition || $[5] !== props.property) {
    t2 = condition ?? createEmptyCondition(props.property);
    $[4] = condition;
    $[5] = props.property;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== condition || $[8] !== props) {
    t3 = next => props.onChange([...props.conditions.filter(x => x !== condition), next]);
    $[7] = condition;
    $[8] = props;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== props.property || $[11] !== t2 || $[12] !== t3) {
    t4 = <Stack sx={t1}><FilterOperatorSelect condition={t2} property={props.property} onChange={t3} /></Stack>;
    $[10] = props.property;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
};
const TagsConditionBody = props => {
  const $ = _c(31);
  const {
    isScrolledVertically,
    setIsScrolledVertically
  } = useScrollable();
  let t0;
  if ($[0] !== isScrolledVertically || $[1] !== setIsScrolledVertically) {
    t0 = event => {
      const isScrolled = event.currentTarget.scrollTop > 0;
      if (isScrolled !== isScrolledVertically) {
        setIsScrolledVertically(isScrolled);
      }
    };
    $[0] = isScrolledVertically;
    $[1] = setIsScrolledVertically;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const onScroll = t0;
  const filteredValue = props.initialFilterMode === "key" ? props.keyFilter : `${props.keyFilter}=${props.valueFilter}`;
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: "12px"
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props) {
    t2 = props.panelHost ? <TagsOperatorSelect {...props} /> : <Typography className="my-[12px]" variant="body1">{`Select one or more ${pluralize(props.entityLabel)}`}</Typography>;
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== filteredValue || $[7] !== props) {
    t3 = !props.panelHost && <AddButton disabled={props.selectedResources.includes(filteredValue) || !props.keyFilter} onClick={() => {
      props.onChange(withSelectedTags(props, [...props.selectedResources, filteredValue]));
    }} />;
    $[6] = filteredValue;
    $[7] = props;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== props.initialFilterMode || $[10] !== props.keyFilter || $[11] !== props.setKeyFilter || $[12] !== props.setValueFilter || $[13] !== props.valueFilter || $[14] !== t3) {
    t4 = <TagSearchBoxes mode={props.initialFilterMode} keyFilter={props.keyFilter} valueFilter={props.valueFilter} onKeyFilterChange={props.setKeyFilter} onValueFilterChange={props.setValueFilter} action={t3} />;
    $[9] = props.initialFilterMode;
    $[10] = props.keyFilter;
    $[11] = props.setKeyFilter;
    $[12] = props.setValueFilter;
    $[13] = props.valueFilter;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== t2 || $[17] !== t4) {
    t5 = <ScrollableHeader hideBorderWhenOnTop={true} className="px-[24px] pb-[24px] py-[12px]" sx={t1}>{t2}{t4}</ScrollableHeader>;
    $[16] = t2;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== props.options.length || $[20] !== props.panelHost) {
    t6 = props.options.length === 0 && <Typography variant="body1" className="my-[12px]">{props.panelHost ? "No matching values found. Try a new search term." : "No matching values found. Try a new search term, or add this value manually to include it once it\u2019s discovered."}</Typography>;
    $[19] = props.options.length;
    $[20] = props.panelHost;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== onScroll || $[23] !== props) {
    t7 = props.options.length > 0 && <MultiSelectFilter {...props} data-testid={`filter-${props.property.name}`} showSelectAll={!props.panelHost && (!!props.keyFilter || !!props.valueFilter)} onVirtuosoScroll={onScroll} virtuosoStyles={{
      height: props.selectedResources.length ? props.initialFilterMode === "key-value" ? "226px" : "270px" : props.initialFilterMode === "key-value" ? "286px" : "330px",
      marginRight: "-24px",
      paddingRight: "24px"
    }} />;
    $[22] = onScroll;
    $[23] = props;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== t6 || $[26] !== t7) {
    t8 = <ScrollableContent className="px-[24px] py-[12px]">{t6}{t7}</ScrollableContent>;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== t5 || $[29] !== t8) {
    t9 = <>{t5}{t8}</>;
    $[28] = t5;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
};
function _temp(item_0) {
  return <Typography>{item_0}</Typography>;
}
function _temp2(item_1) {
  return item_1;
}